/**
 * Tree walking and message filtering for ChatGPT conversation trees.
 * Direct port of Python _walk_conversation_tree, _extract_message_text,
 * and message filtering logic.
 *
 * @module tree-walker
 */

/** @typedef {import('./types.js').TreeNode} TreeNode */
/** @typedef {import('./types.js').FilteredMessage} FilteredMessage */
/** @typedef {import('./types.js').ImagePart} ImagePart */
/** @typedef {import('./types.js').ContentProfile} ContentProfile */

/** Content types that contain user-visible text */
const VISIBLE_CONTENT_TYPES = new Set(['text', 'multimodal_text']);

/** Content types from tool calls (browsing, code exec, etc.) */
const TOOL_CONTENT_TYPES = new Set([
  'tether_browsing_display', 'code', 'tether_quote', 'execution_output',
]);

/**
 * Walk from currentNode to root via parent pointers, return chronological node list.
 *
 * Handles branching by following the path from currentNode (the user's final
 * selected branch) back to root, then reversing for chronological order.
 * Falls back to BFS-deepest-leaf if currentNode is missing or invalid.
 *
 * @param {Object<string, TreeNode>} mapping - The conversation tree
 * @param {string|null} currentNode - Node ID of the final selected message
 * @returns {TreeNode[]} Chronological node list (root → leaf)
 */
export function walkConversationTree(mapping, currentNode) {
  if (!mapping || typeof mapping !== 'object') return [];

  // Primary path: walk from currentNode to root
  if (currentNode && currentNode in mapping) {
    const path = [];
    let nodeId = currentNode;
    const visited = new Set();

    while (nodeId && nodeId in mapping && !visited.has(nodeId)) {
      visited.add(nodeId);
      path.push(mapping[nodeId]);
      nodeId = mapping[nodeId].parent ?? null;
    }

    path.reverse();
    return path;
  }

  // Fallback: find roots → BFS to deepest leaf → walk back
  const keys = Object.keys(mapping);
  if (keys.length === 0) return [];

  const roots = keys.filter(
    (nid) =>
      mapping[nid].parent == null || !(mapping[nid].parent in mapping)
  );

  if (roots.length === 0) return [];

  // BFS to find deepest node
  let deepest = roots[0];
  const queue = [...roots];
  while (queue.length > 0) {
    const nid = queue.shift();
    deepest = nid;
    const children = mapping[nid].children || [];
    for (const childId of children) {
      if (childId in mapping) {
        queue.push(childId);
      }
    }
  }

  // Walk back from deepest to root
  const path = [];
  let nodeId = deepest;
  const visited = new Set();
  while (nodeId && nodeId in mapping && !visited.has(nodeId)) {
    visited.add(nodeId);
    path.push(mapping[nodeId]);
    nodeId = mapping[nodeId].parent ?? null;
  }

  path.reverse();
  return path;
}

/**
 * Extract readable text from a message's content.
 *
 * For multimodal_text, joins string parts and replaces non-text parts
 * with placeholder markers.
 *
 * @param {import('./types.js').Message} message
 * @returns {string}
 */
export function extractMessageText(message) {
  const content = message.content || {};
  const contentType = content.content_type || '';

  if (contentType === 'text') {
    const parts = content.parts || [];
    return parts.filter((p) => typeof p === 'string').join('\n');
  }

  if (contentType === 'multimodal_text') {
    const pieces = [];
    for (const part of content.parts || []) {
      if (typeof part === 'string') {
        pieces.push(part);
      } else if (typeof part === 'object' && part !== null) {
        const partType = part.content_type || '';
        if (partType.includes('image')) {
          pieces.push('[Image]');
        } else if (part.name) {
          pieces.push(`[Attachment: ${part.name}]`);
        } else {
          pieces.push('[Attachment]');
        }
      }
    }
    return pieces.join('\n');
  }

  return '';
}

/**
 * Calculate vision token cost for a single image using OpenAI's tile formula.
 * Falls back to 765 tokens (1024×1024 default) when dimensions are missing.
 *
 * @param {number} width
 * @param {number} height
 * @returns {number}
 */
export function calculateImageTokens(width, height) {
  if (!width || !height || width <= 0 || height <= 0) return 765;

  // Step 1: Scale to fit 2048×2048
  if (width > 2048 || height > 2048) {
    const scale = Math.min(2048 / width, 2048 / height);
    width = Math.floor(width * scale);
    height = Math.floor(height * scale);
  }

  // Step 2: Scale shortest side to 768
  const shortest = Math.min(width, height);
  if (shortest > 768) {
    const scale = 768 / shortest;
    width = Math.floor(width * scale);
    height = Math.floor(height * scale);
  }

  // Step 3-4: Count tiles, compute tokens
  const tiles = Math.ceil(width / 512) * Math.ceil(height / 512);
  return 85 + 170 * tiles;
}

/**
 * Extract image parts from a message's content.
 * Works on ANY message regardless of role/weight.
 *
 * @param {import('./types.js').Message} message
 * @returns {ImagePart[]}
 */
export function extractImageParts(message) {
  const content = message?.content || {};
  const parts = content.parts || [];
  const images = [];
  for (const part of parts) {
    if (typeof part === 'object' && part !== null) {
      const ct = part.content_type || '';
      if (ct.includes('image')) {
        images.push({
          width: part.width || 0,
          height: part.height || 0,
          tokens: calculateImageTokens(part.width, part.height),
        });
      }
    }
  }
  return images;
}

/**
 * Scan all tree nodes for images (including tool/weight:0 messages).
 * Returns conversation-level image inventory.
 *
 * @param {TreeNode[]} nodes
 * @returns {{totalImageTokens: number, imageCount: number, userImageCount: number, generatedImageCount: number}}
 */
export function scanConversationImages(nodes) {
  let totalImageTokens = 0;
  let imageCount = 0;
  let userImageCount = 0;
  let generatedImageCount = 0;

  for (const node of nodes) {
    const msg = node.message;
    if (!msg) continue;
    const images = extractImageParts(msg);
    if (images.length === 0) continue;

    const role = (msg.author || {}).role || '';
    for (const img of images) {
      imageCount++;
      totalImageTokens += img.tokens;
      if (role === 'user') userImageCount++;
      else generatedImageCount++;
    }
  }

  return { totalImageTokens, imageCount, userImageCount, generatedImageCount };
}

/**
 * Detect code blocks and tables in message text.
 * Code blocks: fenced with triple backticks.
 * Tables: markdown tables (header row + separator row + data rows).
 *
 * @param {string} text
 * @returns {ContentProfile}
 */
export function detectContentTypes(text) {
  let codeBlockCount = 0;
  let codeBlockChars = 0;
  let tableCount = 0;
  let tableChars = 0;

  if (!text) return { codeBlockCount, codeBlockChars, tableCount, tableChars };

  // Code blocks: ```...```
  const codeBlockRe = /```[\s\S]*?```/g;
  let match;
  while ((match = codeBlockRe.exec(text)) !== null) {
    codeBlockCount++;
    codeBlockChars += match[0].length;
  }

  // Tables: header row with pipes, followed by separator row with dashes
  const lines = text.split(/\r?\n/);
  let i = 0;
  while (i < lines.length - 1) {
    const line = lines[i];
    const nextLine = lines[i + 1];
    // Header: contains at least one pipe and non-whitespace
    // Separator: pipes and dashes/colons/spaces only
    if (/\|.+\|/.test(line) && /^\s*\|[-:\s|]+\|\s*$/.test(nextLine)) {
      let tableStart = i;
      let charCount = lines[i].length + 1 + lines[i + 1].length + 1; // +1 for newlines
      let j = i + 2;
      // Forward scan: contiguous rows starting with |
      while (j < lines.length && /^\s*\|/.test(lines[j])) {
        charCount += lines[j].length + 1;
        j++;
      }
      tableCount++;
      tableChars += charCount;
      i = j;
    } else {
      i++;
    }
  }

  return { codeBlockCount, codeBlockChars, tableCount, tableChars };
}

/**
 * Count tool call messages (browsing, code execution, quotes) across all nodes.
 * These are filtered out of visible messages but still consume context.
 *
 * @param {TreeNode[]} nodes
 * @returns {{toolCallCount: number, toolCallChars: number}}
 */
export function countToolCalls(nodes) {
  let toolCallCount = 0;
  let toolCallChars = 0;

  for (const node of nodes) {
    const msg = node.message;
    if (!msg) continue;

    const content = msg.content || {};
    const contentType = content.content_type || '';
    if (!TOOL_CONTENT_TYPES.has(contentType)) continue;

    toolCallCount++;

    // Tool messages store text in different fields depending on type:
    //   code → content.text (code interpreter source code)
    //   tether_browsing_display → content.result (fetched page content)
    //   tether_quote → content.text (quoted excerpt)
    // Also check parts[] as fallback for any format variants.
    if (typeof content.text === 'string') {
      toolCallChars += content.text.length;
    }
    if (typeof content.result === 'string') {
      toolCallChars += content.result.length;
    }
    const parts = content.parts || [];
    for (const part of parts) {
      if (typeof part === 'string') {
        toolCallChars += part.length;
      }
    }
  }

  return { toolCallCount, toolCallChars };
}

/**
 * Count system message tokens across all nodes.
 * System messages consume context but aren't shown to the user.
 *
 * @param {TreeNode[]} nodes
 * @returns {number} Estimated system token count
 */
export function countSystemTokens(nodes) {
  let totalChars = 0;

  for (const node of nodes) {
    const msg = node.message;
    if (!msg) continue;

    const role = (msg.author || {}).role || '';
    if (role !== 'system') continue;

    const text = extractMessageText(msg);
    totalChars += text.length;
  }

  return Math.ceil(totalChars / 4);
}

/**
 * Filter tree nodes to visible user/assistant messages.
 *
 * Five filters applied in order (ALL must pass):
 * 1. node.message !== null
 * 2. message.weight !== 0.0 (default 1.0 if missing)
 * 3. author.role is "user" or "assistant"
 * 4. content.content_type is "text" or "multimodal_text"
 * 5. Extracted text is non-empty after .trim()
 *
 * @param {TreeNode[]} nodes - Chronological node list from walkConversationTree
 * @returns {FilteredMessage[]}
 */
export function filterMessages(nodes) {
  const messages = [];

  for (const node of nodes) {
    const msg = node.message;

    // Filter 1: message exists
    if (msg == null) continue;

    // Filter 2: weight !== 0 (edited/retracted)
    const weight = msg.weight ?? 1.0;
    if (weight === 0.0) continue;

    // Filter 3: user or assistant only
    const role = (msg.author || {}).role || '';
    if (role !== 'user' && role !== 'assistant') continue;

    // Filter 4: visible content type
    const contentType = (msg.content || {}).content_type || '';
    if (!VISIBLE_CONTENT_TYPES.has(contentType)) continue;

    // Filter 5: non-empty text
    const text = extractMessageText(msg);
    if (!text.trim()) continue;

    const textTokens = Math.ceil(text.length / 4);
    const imageParts = extractImageParts(msg);
    const imageTokens = imageParts.reduce((sum, img) => sum + img.tokens, 0);

    const contentProfile = detectContentTypes(text);
    const plainChars = text.length - contentProfile.codeBlockChars - contentProfile.tableChars;
    const renderChars = Math.max(0, plainChars) * 1.0
      + contentProfile.codeBlockChars * 12.0
      + contentProfile.tableChars * 18.0
      + imageParts.length * 5000;

    messages.push({
      role,
      text,
      timestamp: msg.create_time ?? null,
      updateTime: msg.update_time ?? null,
      tokens: textTokens + imageTokens,
      imageTokens,
      contentProfile,
      renderChars,
    });
  }

  return messages;
}
